import { useEffect, useMemo, useRef } from "react";
import { isLandmark } from "../bounds.js";
import { useWorldT } from "../i18n.js";
import { floorRows, roomLayout } from "../interior/layout.js";
import { useWorldState } from "../store.js";
import type { WorldStore } from "../store.js";
import { useNavState } from "./nav.js";
import type { NavStore } from "./nav.js";

const WHEEL_GAP_MS = 380;

/**
 * Floor navigation, one component for every place a floor is chosen with the mouse:
 *  - inside a building: the elevator (floor list, up and down, wheel, exit);
 *  - outside, with a building selected: the same list opens a cutaway of that floor (the building
 *    is peeled down to the floor and the camera frames it), no need to walk in;
 *  - outside, cutaway open: the list keeps working and offers "enter" and "close".
 * Members only (the same rule as walking in); everybody else sees no list.
 */
export function FloorNav({ store, nav }: { store: WorldStore; nav: NavStore }) {
  const t = useWorldT();
  const s = useNavState(nav);
  const world = useWorldState(store);
  const box = useRef<HTMLElement>(null);

  const selected = world.selected ? store.node(world.selected.id) : undefined;
  const pickable =
    !s.inside &&
    !s.cutaway &&
    s.mode === "orbit" &&
    selected !== undefined &&
    selected.parentId === null &&
    selected.geometry.kind === "procedural" &&
    selected.geometry.generator === "building.box" &&
    nav.entryOf(selected.id).ok;
  const id = s.inside?.id ?? s.cutaway?.id ?? (pickable ? selected.id : null);
  const node = id ? store.node(id) : undefined;
  const current = s.inside?.level ?? s.cutaway?.level ?? null;

  const building = useMemo(
    () => world.org?.companies.flatMap((c) => c.buildings).find((b) => b.id === id) ?? null,
    [world.org, id],
  );
  const rows = useMemo(() => {
    if (!node || node.geometry.kind !== "procedural") return [];
    return floorRows(roomLayout(node.geometry.params), building);
  }, [node, building]);
  const levels = useMemo(() => rows.map((r) => r.level), [rows]);

  const pick = (level: number) => {
    if (!id) return;
    if (s.inside) void nav.goToFloor(level);
    else nav.showFloor(id, level);
  };
  const step = (delta: 1 | -1) => {
    if (current === null) {
      pick(levels.includes(0) ? 0 : (levels[0] ?? 0));
      return;
    }
    void nav.stepFloor(delta, levels);
  };
  // The wheel over the panel changes floor (a native listener: it must stop the page scrolling).
  const stepRef = useRef(step);
  stepRef.current = step;
  const lastWheel = useRef(0);
  const shown = id !== null && rows.length > 0;
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const now = performance.now();
      if (now - lastWheel.current < WHEEL_GAP_MS || e.deltaY === 0) return;
      lastWheel.current = now;
      stepRef.current(e.deltaY < 0 ? 1 : -1);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [shown]);
  useEffect(() => {
    box.current
      ?.querySelector<HTMLElement>('[aria-current="true"]')
      ?.scrollIntoView?.({ block: "nearest" });
  }, [current, shown]);

  if (!node || !shown) return null;
  const name = isLandmark(node) ? t("panel.landmarkTitle") : (node.label ?? t("target.unknown"));
  const top = levels[levels.length - 1];
  const bottom = levels[0];

  return (
    <aside
      ref={box}
      className="world-elevator"
      data-testid="world-elevator"
      data-scope={s.inside ? "inside" : s.cutaway ? "cutaway" : "outside"}
      aria-label={t("interior.elevator")}
    >
      <p className="world-elevator-title">{s.inside ? t("interior.inside", { name }) : name}</p>
      <div className="world-elevator-head">
        <h3>{t("interior.elevator")}</h3>
        <span className="world-elevator-steps">
          <button
            type="button"
            data-testid="floor-up"
            aria-label={t("interior.floorUp")}
            title={t("interior.floorUp")}
            disabled={current !== null && current === top}
            onClick={() => step(1)}
          >
            ▲
          </button>
          <button
            type="button"
            data-testid="floor-down"
            aria-label={t("interior.floorDown")}
            title={t("interior.floorDown")}
            disabled={current !== null && current === bottom}
            onClick={() => step(-1)}
          >
            ▼
          </button>
        </span>
      </div>
      <ul>
        {[...rows].reverse().map((r) => (
          <li key={r.level}>
            <button
              type="button"
              aria-current={r.level === current ? "true" : undefined}
              data-testid={`floor-${r.level}`}
              title={t("interior.goTo", { level: r.level })}
              onClick={() => pick(r.level)}
            >
              <strong>{r.level}</strong>
              <span className="world-floor-label">
                {r.name ? (
                  <>
                    <b>{r.name}</b>
                    <small>{t("interior.floorTitle", { level: r.level })}</small>
                  </>
                ) : (
                  t("interior.floorTitle", { level: r.level })
                )}
              </span>
            </button>
          </li>
        ))}
      </ul>
      <p className="world-elevator-hint">
        {s.inside
          ? t("interior.wheelHint")
          : s.cutaway
            ? t("interior.cutawayHint")
            : t("interior.peelHint")}
      </p>
      {s.inside ? (
        <button
          type="button"
          className="world-exit"
          data-testid="world-exit"
          onClick={() => void nav.exit()}
        >
          {t("interior.exitBuilding")} <kbd>E</kbd>
        </button>
      ) : s.cutaway ? (
        <div className="world-elevator-actions">
          <button
            type="button"
            className="world-exit is-primary"
            data-testid="world-cutaway-enter"
            onClick={() => void nav.enter(s.cutaway!.id)}
          >
            {t("nav.enterSelected")}
          </button>
          <button
            type="button"
            className="world-exit"
            data-testid="world-cutaway-close"
            onClick={() => nav.closeCutaway()}
          >
            {t("interior.closeCutaway")}
          </button>
        </div>
      ) : null}
    </aside>
  );
}
